import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'expo-router';
import useOrganizationSetup from '@/utils/hooks/useOrganizationSetup';
import useAchievementStore from '@/data/store/useAchievementStore';
import { canManageOrganizationSetup, newSetupCompletions, ORGANIZATION_SETUP_TASKS, setupProgress } from '@/config/organizationSetup';
import { QueryKeys } from '@/utils/react-query/QueryKeys';

export default function OrganizationSetupWatcher({ user, enabled }) {
    const query = useOrganizationSetup(user, { enabled, poll: true });
    const previous = useRef(null);
    const enqueue = useAchievementStore(state => state.enqueue);
    const queryClient = useQueryClient();
    const pathname = usePathname();
    useEffect(() => {
        if (!enabled || !canManageOrganizationSetup(user) || !query.isFetchedAfterMount || !query.data) return;
        const { resolved, total } = setupProgress(query.data);
        for (const item of newSetupCompletions(previous.current, query.data)) {
            enqueue({
                id: `setup:${query.data.organizationID}:${item.id}:${item.resolvedAt}`,
                kind: 'setup',
                scope: `${user.accountID}:${user.organizationID}`,
                title: ORGANIZATION_SETUP_TASKS[item.id].achievement,
                message: `${resolved} of ${total} setup tasks resolved${resolved === total ? '. Your setup checklist is finished!' : '.'}`,
            });
        }
        previous.current = query.data;
    }, [enabled, query.data, query.isFetchedAfterMount, enqueue, user]);
    // Direct data helpers (imports/settings) and other administrators are
    // picked up after navigation as well as by the foreground polling fallback.
    useEffect(() => {
        if (enabled) queryClient.invalidateQueries({ queryKey: QueryKeys.organizationSetup.state(user?.organizationID, user?.accountID) });
    }, [enabled, queryClient, user?.organizationID, user?.accountID, pathname]);
    // Reviewing Organization settings is itself the task; the RPC still
    // verifies a saved name and valid timezone before completing it.
    const reviewPending = query.data?.items?.some(item => item.id === 'organization' && item.status === 'pending');
    const reviewed = useRef(null);
    useEffect(() => {
        if (enabled && canManageOrganizationSetup(user) && reviewPending
            && reviewed.current !== user?.organizationID
            && pathname?.replace(/\/+$/, '') === '/settings/organization') {
            reviewed.current = user?.organizationID;
            query.resolve.mutate({ taskID: 'organization', status: 'completed' });
        }
    }, [enabled, user, reviewPending, pathname, query.resolve]);
    return null;
}
